import React, { useEffect, useState } from "react";
import strings from "translate/language";
import PasswordResetEmailPreview from "component/common/PasswordResetEmailPreview";
import "./JustoPasswordResetModal.css";

/**
 * Confirm single-user password reset (never bulk).
 * Shows target user details, warnings, and optional email send.
 */
const JustoPasswordResetModal = ({
  open,
  onClose,
  onConfirm,
  userName = "",
  email = "",
  mobile = "",
  submitting = false,
}) => {
  const [sendEmail, setSendEmail] = useState(false);

  useEffect(() => {
    if (open) {
      setSendEmail(false);
    }
  }, [open]);

  useEffect(() => {
    if (!open) {
      return undefined;
    }
    const onKeyDown = (event) => {
      if (event.key === "Escape" && !submitting) {
        onClose();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open, submitting, onClose]);

  if (!open) {
    return null;
  }

  const displayName = String(userName || "").trim() || strings.notavailable;
  const displayEmail = String(email || "").trim();
  const displayMobile = String(mobile || "").trim();
  const initials = displayName
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join("");

  return (
    <div
      className="justo-pwr-modal__backdrop"
      onClick={() => !submitting && onClose()}
      role="presentation"
    >
      <div
        className={`justo-pwr-modal${
          sendEmail && displayEmail ? " justo-pwr-modal--with-preview" : ""
        }`}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="justo-password-reset-title"
      >
        <div className="justo-pwr-modal__header">
          <div className="justo-pwr-modal__icon" aria-hidden="true">
            <i className="fa fa-key" />
          </div>
          <div className="justo-pwr-modal__header-copy">
            <h2
              className="justo-pwr-modal__title"
              id="justo-password-reset-title"
            >
              {strings.resetPasswordConfirmTitle}
            </h2>
            <p className="justo-pwr-modal__subtitle">
              {strings.resetPasswordConfirmIntro}
            </p>
          </div>
          <button
            type="button"
            className="justo-pwr-modal__close"
            onClick={() => !submitting && onClose()}
            aria-label="Close"
            disabled={submitting}
          >
            ×
          </button>
        </div>

        <div className="justo-pwr-modal__scroll">
          <div className="justo-pwr-modal__body">
            <div className="justo-pwr-modal__col justo-pwr-modal__col--user">
              <p className="justo-pwr-modal__section-label">
                {strings.resetPasswordUserLabel}
              </p>
              <div className="justo-pwr-modal__user">
                <div className="justo-pwr-modal__avatar" aria-hidden="true">
                  {initials || "?"}
                </div>
                <div className="justo-pwr-modal__user-meta">
                  <p className="justo-pwr-modal__user-name">{displayName}</p>
                  <div className="justo-pwr-modal__user-lines">
                    <span className="justo-pwr-modal__user-line">
                      <i className="fa fa-envelope-o" aria-hidden="true" />
                      {displayEmail || strings.notavailable}
                    </span>
                    <span className="justo-pwr-modal__user-line">
                      <i className="fa fa-phone" aria-hidden="true" />
                      {displayMobile || strings.notavailable}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div className="justo-pwr-modal__col justo-pwr-modal__col--details">
              <p className="justo-pwr-modal__section-label">
                {strings.resetPasswordWhatHappens}
              </p>
              <ul className="justo-pwr-modal__steps">
                <li className="justo-pwr-modal__step">
                  <span className="justo-pwr-modal__step-icon" aria-hidden="true">
                    <i className="fa fa-lock" />
                  </span>
                  <span>{strings.resetPasswordWarningDefault}</span>
                </li>
                <li className="justo-pwr-modal__step">
                  <span
                    className="justo-pwr-modal__step-icon justo-pwr-modal__step-icon--ok"
                    aria-hidden="true"
                  >
                    <i className="fa fa-user" />
                  </span>
                  <span>{strings.resetPasswordWarningSingleOnly}</span>
                </li>
                <li className="justo-pwr-modal__step">
                  <span
                    className={`justo-pwr-modal__step-icon ${
                      sendEmail
                        ? "justo-pwr-modal__step-icon--mail"
                        : "justo-pwr-modal__step-icon--mute"
                    }`}
                    aria-hidden="true"
                  >
                    <i
                      className={`fa ${sendEmail ? "fa-envelope" : "fa-ban"}`}
                    />
                  </span>
                  <span>
                    {sendEmail
                      ? strings.resetPasswordWarningWithEmail
                      : strings.resetPasswordWarningNoEmail}
                  </span>
                </li>
              </ul>

              <button
                type="button"
                className={`justo-pwr-modal__toggle ${
                  sendEmail ? "justo-pwr-modal__toggle--on" : ""
                }`}
                disabled={submitting || !displayEmail}
                onClick={() => setSendEmail((v) => !v)}
                aria-pressed={sendEmail}
              >
                <span className="justo-pwr-modal__toggle-copy">
                  <span className="justo-pwr-modal__toggle-title">
                    {strings.sendPasswordEmail}
                  </span>
                  <span className="justo-pwr-modal__toggle-hint">
                    {displayEmail
                      ? strings.resetPasswordEmailToggleHint
                      : strings.resetPasswordEmailMissing}
                  </span>
                </span>
                <span className="justo-pwr-modal__switch" aria-hidden="true">
                  <span className="justo-pwr-modal__switch-knob" />
                </span>
              </button>
            </div>
          </div>

          {sendEmail && displayEmail ? (
            <div className="justo-pwr-modal__preview">
              <PasswordResetEmailPreview recipientName={displayName} />
            </div>
          ) : null}
        </div>

        <div className="justo-pwr-modal__footer">
          <button
            type="button"
            className="justo-pwr-modal__btn justo-pwr-modal__btn--ghost"
            disabled={submitting}
            onClick={onClose}
          >
            {strings.cancel}
          </button>
          <button
            type="button"
            className="justo-pwr-modal__btn justo-pwr-modal__btn--solid"
            disabled={submitting}
            onClick={() => onConfirm({ send_email: sendEmail })}
          >
            <i className="fa fa-check" aria-hidden="true" />
            {submitting ? strings.resetting : strings.confirmResetPassword}
          </button>
        </div>
      </div>
    </div>
  );
};

export default JustoPasswordResetModal;
